import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { BEVEL_INSET_NAVY } from "@majourneys/shared/ui";
import { PW, pewterPanel, pewterPrimary } from "../internal-ui/pewter-shell";
import { TicketProgress } from "./booking-ticket";

/**
 * The booking sheet's last step, small: "Request sent!", the booking as a
 * mini ticket, the Booked / Confirming / Confirmed line and two buttons,
 * WhatsApp and See details. See details (or dragging the handle up) opens
 * the full confirmation the sheet always showed. Owner, 2026-11-04, from
 * /confirmed-sheet-lab: option A, pewter and playful, and it must read as
 * pending until we have confirmed with the provider.
 *
 * Shared by the activity sheet and the nightclub sheet; the app has its own
 * (apps/mobile/src/features/bookings/components/booking-done-mini.tsx).
 */

export interface BookingDoneMiniProps {
  image?: string | null;
  title: string;
  /** "Sun, 8 Nov · 10:00": the day and, when there is one, the time. */
  when: string;
  /** "2 adults", "Table for 2". */
  party: string;
  /** The short reference, or null while there is none. */
  reference: string | null;
  /** The line under the headline: who we are checking with. */
  sub: string;
  waHref: string;
  /** The category's icon, which drives along the progress line; none, no rider. */
  icon?: string | null;
  /** How it moves while it waits on Confirming (see `doneIconMotion`). */
  iconMotion?: DoneIconMotion;
  onExpand: () => void;
  onClose: () => void;
}

export type DoneIconMotion = "bump" | "sway" | "lope";

/**
 * How a category's icon idles once it has parked on Confirming: a balloon
 * sways, an animal lopes, anything with wheels or a hull bumps.
 */
export function doneIconMotion(categorySlug: string | null | undefined): DoneIconMotion {
  const slug = (categorySlug ?? "").toLowerCase();
  if (slug === "air") return "sway";
  if (slug === "camel" || slug === "horse") return "lope";
  return "bump";
}

const RING =
  "linear-gradient(155deg, #FFFFFF 0%, rgba(180,184,210,0.55) 28%, rgba(17,14,57,0.18) 55%, rgba(180,184,210,0.55) 78%, #FFFFFF 100%)";
const CONFETTI = ["#E5B265", "#110E39", "#1fa855", "#7d6cf5", "#F0C987", "#2E2878"];

/** The WhatsApp green with the same soft bevel as the navy button beside it. */
export const DONE_WA_BUTTON: CSSProperties = {
  background: "linear-gradient(180deg, #2fd774 0%, #1fb158 100%)",
  color: "#fff",
  boxShadow: `${BEVEL_INSET_NAVY}, 0 0 0 1px #17994a, 0 10px 22px -12px rgba(37,211,102,0.75)`,
};

/*
 * The motion, in order: the tick pops into its ring and draws itself, the
 * confetti bursts from it, the ticket drops in at a slight tilt, the green
 * line fills toward Confirming, the gold dot pops on and keeps pulsing (its
 * ping is TicketProgress's own). The progress selectors reach into
 * TicketProgress: its bar row is the aria-hidden flex, second child the
 * green line, third the gold dot.
 */
const DONE_MINI_CSS = `
@keyframes mjDonePop { 0% { transform: scale(0) } 100% { transform: scale(1) } }
@keyframes mjDoneDraw { to { stroke-dashoffset: 0 } }
@keyframes mjDoneDrop { 0% { opacity: 0; transform: translateY(18px) rotate(-3deg) } 100% { opacity: 1; transform: none } }
@keyframes mjDoneRise { 0% { opacity: 0; transform: translateY(10px) } 100% { opacity: 1; transform: none } }
@keyframes mjDoneGrow { 0% { transform: scaleX(0) } 100% { transform: scaleX(1) } }
@keyframes mjDoneFly { 0% { opacity: 1; transform: translate(0,0) rotate(0) } 100% { opacity: 0; transform: translate(var(--x), var(--y)) rotate(var(--r)) } }
.mj-done-ring { animation: mjDonePop 520ms 160ms cubic-bezier(.34,1.8,.5,1) both }
.mj-done-tick { animation: mjDoneDraw 320ms 480ms ease-out both }
.mj-done-ticket { animation: mjDoneDrop 560ms 300ms cubic-bezier(.34,1.4,.5,1) both }
.mj-done-progress { animation: mjDoneRise 340ms 560ms ease-out both }
.mj-done-progress > [aria-hidden] > span:nth-child(2) { transform-origin: left center; animation: mjDoneGrow 1100ms 700ms cubic-bezier(.45,.05,.3,1) both }
[dir="rtl"] .mj-done-progress > [aria-hidden] > span:nth-child(2) { transform-origin: right center }
.mj-done-progress > [aria-hidden] > span:nth-child(3) { animation: mjDonePop 420ms 1720ms cubic-bezier(.34,1.8,.5,1) both }
.mj-done-actions { animation: mjDoneRise 380ms 640ms ease-out both }
.mj-done-btn { transition: transform 120ms ease-out }
.mj-done-btn:active { transform: scale(0.97) }
@media (prefers-reduced-motion: reduce) {
  .mj-done-ring, .mj-done-tick, .mj-done-ticket, .mj-done-progress, .mj-done-actions,
  .mj-done-progress > [aria-hidden] > span { animation: none !important }
  .mj-done-tick { stroke-dashoffset: 0 !important }
}
`;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Confetti from the tick, portalled to the body: the sheet clips (rounded,
 * overflow hidden) and carries a transform, which would trap a fixed layer
 * inside it, and most of the burst flies up, over the page.
 */
function Confetti({ origin }: { origin: { x: number; y: number } | null }) {
  const [pieces, setPieces] = useState<{ id: number; style: CSSProperties }[]>([]);
  useEffect(() => {
    if (!origin || prefersReducedMotion()) return;
    setPieces(
      Array.from({ length: 34 }, (_, i) => {
        const a = -Math.PI * (0.08 + Math.random() * 0.84);
        const r = 110 + Math.random() * 170;
        return {
          id: i,
          style: {
            position: "absolute",
            background: CONFETTI[i % CONFETTI.length],
            width: i % 3 === 0 ? 8 : 6,
            height: i % 3 === 0 ? 8 : 12,
            borderRadius: i % 3 === 0 ? 999 : 2,
            ["--x" as string]: `${Math.cos(a) * r}px`,
            ["--y" as string]: `${Math.sin(a) * r + 120}px`,
            ["--r" as string]: `${Math.random() * 720 - 360}deg`,
            animation: `mjDoneFly ${1000 + Math.random() * 600}ms ${Math.random() * 90}ms cubic-bezier(.12,.75,.35,1) both`,
          },
        };
      }),
    );
    const id = window.setTimeout(() => setPieces([]), 1900);
    return () => window.clearTimeout(id);
  }, [origin]);
  if (!origin || pieces.length === 0 || typeof document === "undefined") return null;
  return createPortal(
    <div
      aria-hidden
      data-testid="done-confetti"
      className="pointer-events-none fixed z-[calc(var(--z-modal)+2)]"
      style={{ left: origin.x, top: origin.y }}
    >
      {pieces.map((p) => (
        <span key={p.id} style={p.style} />
      ))}
    </div>,
    document.body,
  );
}

export function BookingDoneMini({
  image,
  title,
  when,
  party,
  reference,
  sub,
  waHref,
  icon,
  iconMotion = "bump",
  onExpand,
  onClose,
}: BookingDoneMiniProps) {
  const { t } = useTranslation();
  const tickRef = useRef<HTMLDivElement>(null);
  const [burst, setBurst] = useState<{ x: number; y: number } | null>(null);

  // Fire once the tick has popped in, from wherever it sits on screen (the
  // sheet may still be settling, so read it late rather than on mount).
  useEffect(() => {
    const id = window.setTimeout(() => {
      const r = tickRef.current?.getBoundingClientRect();
      if (r) setBurst({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    }, 380);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <div className="relative px-5 pb-5 pt-2 sm:pt-5" data-testid="booking-done-mini">
      <style>{DONE_MINI_CSS}</style>
      <Confetti origin={burst} />

      <div className="flex items-center gap-3">
        <div ref={tickRef} className="mj-done-ring shrink-0 rounded-full p-[3px]" style={{ background: RING }}>
          <div
            className="flex h-11 w-11 items-center justify-center rounded-full"
            style={{ background: "linear-gradient(180deg, #28c46a 0%, #1A7A44 100%)", boxShadow: BEVEL_INSET_NAVY }}
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden>
              <path
                className="mj-done-tick"
                d="M5 12.5l4.2 4.2L19 7"
                fill="none"
                stroke="#fff"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="24"
                strokeDashoffset="24"
              />
            </svg>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-[22px] font-bold leading-[1.15] text-[#110E39]">
            {t("booking_done.headline", { defaultValue: "Request sent!" })}
          </h3>
          <p className="mt-0.5 font-sans text-[13px] font-medium text-[#5a5d77]">{sub}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close", { defaultValue: "Close" })}
          className="flex h-8 w-8 shrink-0 items-center justify-center self-start rounded-full text-[#110E39]"
          style={{ background: "rgba(17,14,57,0.06)" }}
        >
          <Icon name="ri-close-line" className="text-[17px]" aria-hidden />
        </button>
      </div>

      <div className="mj-done-ticket mt-4" style={pewterPanel(20)}>
        <div className="flex items-stretch">
          <div className="shrink-0 p-2.5 pe-3" style={{ borderInlineEnd: "1.5px dashed rgba(17,14,57,0.16)" }}>
            <div className="rounded-[14px] p-[3px]" style={{ background: RING }}>
              {image ? (
                <img src={image} alt="" aria-hidden className="h-[60px] w-[60px] rounded-[11px] object-cover" />
              ) : (
                <span className="flex h-[60px] w-[60px] items-center justify-center rounded-[11px] bg-[#f4f1ff] text-[#110E39]">
                  <Icon name="ri-compass-3-line" className="text-[22px]" aria-hidden />
                </span>
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1 px-3 py-2.5">
            <p className="line-clamp-2 break-words font-heading text-[15px] font-bold leading-[1.2] text-[#110E39] [text-wrap:balance]">
              {title}
            </p>
            <p className="mt-1 font-sans text-[12.5px] font-semibold text-[#110E39]">{when}</p>
            <p className="mt-0.5 font-sans text-[12px] font-medium text-[#5a5d77]">
              {party}
              {reference ? (
                <>
                  {" · "}
                  <span dir="ltr">{reference}</span>
                </>
              ) : null}
            </p>
          </div>
        </div>
      </div>

      {/* The category's icon drives from Booked and parks on Confirming,
          idling there while we confirm with the provider (owner, 2026-11-04). */}
      <div className={`mj-done-progress relative px-1${icon ? " pt-[40px]" : ""}`}>
        {icon ? (
          <CategoryRider icon={icon} motion={iconMotion} to="calc(50% - 22px)" />
        ) : null}
        <TicketProgress status="pending" className={icon ? "mt-0" : "mt-4"} />
      </div>

      <div className="mj-done-actions mt-4 grid grid-cols-2 gap-2.5">
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer"
          className="mj-done-btn flex min-w-0 items-center justify-center gap-1.5 rounded-full px-2 py-3 font-sans text-[13.5px] font-bold"
          style={DONE_WA_BUTTON}
        >
          <Icon name="ri-whatsapp-line" className="text-[17px]" aria-hidden />
          {t("booking_done.whatsapp", { defaultValue: "WhatsApp" })}
        </a>
        <button
          type="button"
          onClick={onExpand}
          aria-expanded={false}
          className="mj-done-btn flex min-w-0 items-center justify-center gap-1 rounded-full px-2 py-3 font-sans text-[13.5px] font-bold"
          style={{ ...pewterPrimary, color: "#fff" }}
        >
          {t("booking_done.see_details", { defaultValue: "See details" })}
          <Icon name="ri-arrow-up-s-line" className="text-[17px]" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** The way back from the full confirmation to the small one. */
export function BookingDoneShowLess({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded
      className="mx-auto mt-1 inline-flex min-h-[32px] items-center gap-1 px-3 font-sans text-[12.5px] font-bold"
      style={{ color: PW.muted }}
    >
      <Icon name="ri-arrow-down-s-line" className="text-[16px]" aria-hidden />
      {t("booking_done.show_less", { defaultValue: "Show less" })}
    </button>
  );
}

/**
 * Grows or shrinks a card between its small and full size: measure, change,
 * measure again, then animate the height between the two (a CSS transition
 * cannot run to or from `auto`). A no-op when motion is reduced.
 */
export function animateCardResize(card: HTMLElement | null, change: () => void) {
  if (!card || prefersReducedMotion() || typeof card.animate !== "function") {
    change();
    return;
  }
  const from = card.getBoundingClientRect().height;
  change();
  const to = card.getBoundingClientRect().height;
  if (Math.abs(to - from) < 2) return;
  card.animate([{ height: `${from}px` }, { height: `${to}px` }], {
    duration: 460,
    easing: "cubic-bezier(.32,1.18,.5,1)",
  });
}

/*
 * The category icon on a progress line: it drives in from the start of the
 * line to `--mj-rider-to` and idles there. Its own stylesheet so the booked
 * card on the activity page (booking-bar.tsx) can use it too.
 */
const RIDER_CSS = `
@keyframes mjRiderIn { 0% { inset-inline-start: -13px; opacity: 0 } 10% { opacity: 1 } 100% { inset-inline-start: var(--mj-rider-to); opacity: 1 } }
@keyframes mjRiderBump { 0%,100% { transform: translateY(0) rotate(0) } 25% { transform: translateY(-3px) rotate(-4deg) } 60% { transform: translateY(1px) rotate(2deg) } }
@keyframes mjRiderSway { 0%,100% { transform: translateY(0) rotate(-3deg) } 50% { transform: translateY(-5px) rotate(3deg) } }
@keyframes mjRiderLope { 0%,100% { transform: translateY(0) rotate(0) } 50% { transform: translateY(-4px) rotate(-3deg) } }
.mj-rider { inset-inline-start: var(--mj-rider-to); animation: mjRiderIn 1100ms var(--mj-rider-delay, 700ms) cubic-bezier(.45,.05,.3,1) both }
[dir="rtl"] .mj-rider-face { transform: scaleX(-1) }
.mj-rider-bump { animation: mjRiderBump 450ms ease-in-out infinite }
.mj-rider-sway { animation: mjRiderSway 2400ms ease-in-out infinite }
.mj-rider-lope { animation: mjRiderLope 900ms ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .mj-rider, .mj-rider img { animation: none !important } }
`;

/**
 * The category's icon riding a progress line. Sits at the top of a relative
 * box with 40px of room above the line; `to` is where its start edge parks
 * (the dot's centre less half the icon's 44px).
 */
export function CategoryRider({
  icon,
  motion,
  to,
  delayMs = 700,
}: {
  icon: string;
  motion: DoneIconMotion;
  to: string;
  delayMs?: number;
}) {
  return (
    <>
      <style>{RIDER_CSS}</style>
      <span
        aria-hidden
        data-testid="done-rider"
        className="mj-rider pointer-events-none absolute top-0 h-11 w-11"
        style={{ ["--mj-rider-to" as string]: to, ["--mj-rider-delay" as string]: `${delayMs}ms` } as CSSProperties}
      >
        <span className="mj-rider-face block h-full w-full">
          <img src={icon} alt="" className={`mj-rider-${motion} block h-full w-full object-contain`} />
        </span>
      </span>
    </>
  );
}
